<script setup lang="ts">
import { isAppType, isInstitutionType } from '@shared/composables/useAppContext';
import { apiFetch, apiUrl } from '@shared/http/client';
import { computed, onMounted, ref, watch } from 'vue';
import KgrClearingSelect from '@shared/components/KgrClearingSelect.vue';
import { getClearingOptions } from '@shared/components/KgrClearingSelect.vue';
import type { Clearing } from '@shared/components/KgrClearingSelect.vue';
import KgrSelectControlWithPills from '@shared/components/KgrSelectControlWithPills.vue';
import { getFlattenedDepartments, getTeamsForDepartments } from '@shared/department';
import type { Department } from '@shared/department';
import { useRoute, useRouter } from 'vue-router';
import { parseValidationErrors } from '@shared/http/validationErrors';
import type { ValidationErrors } from '@shared/http/validationErrors';
import DatePicker from '@kigaroo/components/base/date-picker';
import KgrAuthImage from '@shared/components/KgrAuthImage.vue';
import KgrConfirmModal from '@shared/components/KgrConfirmModal.vue';
import KgrEditableImage from '@shared/components/KgrEditableImage.vue';
import KgrImageCropper from '@shared/components/KgrImageCropper.vue';
import KgrInputGroup from '@shared/components/KgrInputGroup.vue';
import KgrLoadingContainer from '@shared/components/KgrLoadingContainer.vue';
import KgrPageTitle from '@shared/components/KgrPageTitle.vue';
import KgrWysiwyg from '@shared/components/KgrWysiwyg.vue';
import { SphSwitch } from '@kigaroo/components/base/switch';
import { useI18n } from 'vue-i18n';
import { useLocalePath } from '@shared/composables/useLocalePath';
import { useUserStore } from '@shared/stores/user';

const localePath = useLocalePath();

interface NewsDetail {
    id: number
    title: string | { original: string }
    teaser: string | { original: string }
    text: string | { original: string }
    hasImage: boolean
    imageApiUrl?: string
    isPublished: boolean
    isEditable: boolean
    publishDate: string
    clearing: Clearing
    groups?: { id: number, name: string }[]
    departments?: { id: number, name: string }[]
    teams?: { id: number, name: string }[]
}

interface Group {
    id: number
    name: string
    isEditable: boolean
}

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const { t } = useI18n();

const newsId = computed(() => (route.params.id ? Number(route.params.id) : null));
const isEdit = computed(() => newsId.value !== null);
const isEmployeeApp = isAppType('employee');
// news-form.component.html:69 gates the group field with *kgrIfInstitutionType='kindergarten';
// clubs target departments/teams instead (see APP_MIGRATE_CLUB_MEMBER_APP.md, News section).
const isKindergarten = !isInstitutionType('club');
const canCreateNews = computed(() => userStore.currentUser?.canCreateNews ?? false);

const existingNews = ref<NewsDetail | null>(null);

// Mirrors Angular: edit gated on news.isEditable; create only in the employee app
// (*kgrIfAppType='employee') with canCreateNews — end_user apps see the hint, not the form.
const notAllowed = computed(() => (isEdit.value ? !existingNews.value?.isEditable : !isEmployeeApp || !canCreateNews.value));

const isLoading = ref(true);
const isSaving = ref(false);
const isDeleting = ref(false);
const serverError = ref<string | null>(null);
const showValidationErrors = ref(false);
const serverValidationErrors = ref<ValidationErrors | null>(null);
const showSaveConfirm = ref(false);
const showDeleteConfirm = ref(false);
// Angular's `kgr-editable-image` never deletes straight from the flyout — it opens „Bild löschen?"
// with a preview first (editable-image.component.html `showConfirmDeleteDialog()`).
const showImageDeleteConfirm = ref(false);
const groups = ref<Group[]>([]);

const title = ref('');
const teaser = ref('');
const text = ref('');
// Deliberate deviation: Angular patches the create form with a hardcoded
// `clearing: news?.clearing ?? 'all_employees'` (news-form.component.ts, since 14fa0d36). That club
// value matches no kindergarten option, so the select looked empty, passed the required check and
// the backend rejected the request. Preselect the first clearing the user may manage instead
// (kindergarten: 'internal', club: 'all_employees'); with none, stay null so the required check blocks
// saving. Watched rather than set on mount: the user may still be loading via initApp().
const clearing = ref<Clearing | null>(null);
const availableClearings = computed(() => getClearingOptions(userStore.currentUser?.newsClearingsAllowedToManage));
watch(
    availableClearings,
    (options) => {
        if (!isEdit.value && clearing.value === null) {
            clearing.value = options[0]?.value ?? null;
        }
    },
    { immediate: true },
);

// Angular `news-form.component.ts`: `unfilteredDepartments$` once, then departments and teams
// derived from it — both filtered on `isEditable`, teams additionally de-duplicated.
const departments = ref<Department[]>([]);
const selectedDepartmentIds = ref<number[]>([]);
const selectedTeamIds = ref<number[]>([]);

async function loadGroups() {
    try {
        const groupsResult = await apiFetch<{ data: Group[] | null }>('/groups');
        groups.value = groupsResult.data ?? [];
    } catch {
        groups.value = [];
    }
}

let departmentsLoaded = false;
async function loadDepartments() {
    if (departmentsLoaded) {
        return;
    }
    departmentsLoaded = true;
    try {
        departments.value = await getFlattenedDepartments();
    } catch {
        departments.value = [];
    }
}

const editableDepartments = computed(() => departments.value.filter((d) => d.isEditable));
const editableTeams = computed(() => {
    const teams = getTeamsForDepartments(departments.value).filter((team) => team.isEditable);
    return [...new Map(teams.map((team) => [team.id, team])).values()];
});

// Angular renders both fields only for these two clearings (news-form.component.html:100).
const showDepartmentAndTeamFields = computed(
    () => clearing.value === 'certain_employees' || clearing.value === 'all_employees_and_certain_members',
);

// Global (cross-field) validator of the Angular form: for those two clearings at least one
// department OR one team is required; the message sits on both fields.
// Fetch the department tree the first time the fields become visible — Angular's async pipe does
// exactly this, and it keeps the request out of every form that never selects those clearings.
watch(showDepartmentAndTeamFields, (visible) => {
    if (visible) {
        void loadDepartments();
    }
});

const departmentOrTeamMissing = computed(
    () => showDepartmentAndTeamFields.value && !selectedDepartmentIds.value.length && !selectedTeamIds.value.length,
);
const selectedGroupIds = ref<number[]>([]);
const publishImmediately = ref(true);
const publishDate = ref('');
const notify = ref(false);
const pendingImageDeletion = ref(false);
const newImageBlob = ref<Blob | null>(null);
const newImagePreviewUrl = ref<string | null>(null);
const showCropper = ref(false);
const cropperSrc = ref<string | null>(null);

const TEASER_MAX_LENGTH = 100;
const teaserError = computed(() =>
    teaser.value.length > TEASER_MAX_LENGTH
        ? t('höchstens %requiredLength% Zeichen erlaubt').replace('%requiredLength%', String(TEASER_MAX_LENGTH))
        : null,
);

const currentImageUrl = computed(() => {
    if (pendingImageDeletion.value || newImagePreviewUrl.value) {
        return null;
    }
    if (!existingNews.value?.hasImage || !existingNews.value.imageApiUrl) {
        return null;
    }
    return `${apiUrl(existingNews.value.imageApiUrl)}?size=news_detail_thumb`;
});

const previewImageUrl = computed(() => newImagePreviewUrl.value ?? currentImageUrl.value);

function getOriginal(val: string | { original: string } | undefined): string {
    if (!val) {
        return '';
    }
    return typeof val === 'string' ? val : val.original;
}

function onImageSelect(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) {
        return;
    }
    if (cropperSrc.value) {
        URL.revokeObjectURL(cropperSrc.value);
    }
    cropperSrc.value = URL.createObjectURL(file);
    showCropper.value = true;
    (event.target as HTMLInputElement).value = '';
}

function onCropperConfirm(blob: Blob) {
    if (newImagePreviewUrl.value) {
        URL.revokeObjectURL(newImagePreviewUrl.value);
    }
    newImageBlob.value = blob;
    newImagePreviewUrl.value = URL.createObjectURL(blob);
    pendingImageDeletion.value = false;
    onCropperCancel();
}

function onCropperCancel() {
    showCropper.value = false;
    if (cropperSrc.value) {
        URL.revokeObjectURL(cropperSrc.value);
        cropperSrc.value = null;
    }
}

function removeImage() {
    newImageBlob.value = null;
    if (newImagePreviewUrl.value) {
        URL.revokeObjectURL(newImagePreviewUrl.value);
        newImagePreviewUrl.value = null;
    }
    pendingImageDeletion.value = true;
    showImageDeleteConfirm.value = false;
}

const editableGroups = computed(() => groups.value.filter((g) => g.isEditable));

// publishDate ist in Angular required, aber disabled (→ kein Pflichtfeld, kein Payload),
// wenn sofort veröffentlicht wird oder die News bereits veröffentlicht ist.
const publishDateDisabled = computed(() => publishImmediately.value || !!existingNews.value?.isPublished);
// Angular's `kgr-form-field` derives the required marker from the projected control
// (`formControl?.hasValidator(Validators.required)`). The date control is only in the DOM once it is
// unhidden, so the asterisk appears exactly then — on the create form („Sofort veröffentlichen" on)
// there is none, on the edit form of a published news there is.
const publishDateVisible = computed(() => !!existingNews.value?.isPublished || !publishImmediately.value);

// Pflichtfelder analog Angular: title, clearing, publishDate (sofern enabled); teaser maxLength(100).
const isTitleValid = computed(() => !!title.value.trim());
const isClearingValid = computed(() => !!clearing.value);
const isTeaserValid = computed(() => !teaserError.value);
const isPublishDateValid = computed(() => publishDateDisabled.value || !!publishDate.value);
const isFormValid = computed(
    () => isTitleValid.value && isClearingValid.value && isTeaserValid.value && isPublishDateValid.value && !departmentOrTeamMissing.value,
);

// Angular's `kgr-form-field` reveals a validator error as soon as the projected control is `dirty`
// (user changed the value) or `touched` (blurred) — `determineErrorVisibility`; a submit attempt
// additionally forces every field via `showErrorsIfPristine`. Without this the messages only
// appeared after the first „Speichern".
const interacted = ref<Record<string, boolean>>({});
function markInteracted(field: string) {
    interacted.value[field] = true;
}
function errorsVisible(field: string): boolean {
    return showValidationErrors.value || interacted.value[field] === true;
}

function clientFieldError(server: string[] | null | undefined, valid: boolean, field: string): string | string[] | null {
    if (server?.length) {
        return server;
    }
    if (errorsVisible(field) && !valid) {
        return t('Angabe benötigt');
    }
    return null;
}

function serverFieldError(field: string): string[] | null {
    const value = serverValidationErrors.value?.[field];
    return Array.isArray(value) ? value : null;
}

const titleErrors = computed(() => clientFieldError(serverFieldError('title'), isTitleValid.value, 'title'));
const clearingErrors = computed(() => clientFieldError(serverFieldError('clearing'), isClearingValid.value, 'clearing'));
const publishDateErrors = computed(() => clientFieldError(serverFieldError('publishDate'), isPublishDateValid.value, 'publishDate'));
// Teaser: serverseitige Meldung hat Vorrang, sonst die Maxlength-Meldung (sofort sichtbar).
const teaserErrors = computed(() => serverFieldError('teaser') ?? teaserError.value);
const textErrors = computed(() => serverFieldError('description'));
// The cross-field message („Team oder Abteilung erforderlich") sits on both fields, exactly as in
// Angular's `globalValidationErrorMessages`.
const departmentOrTeamError = computed(() =>
    (errorsVisible('departmentIds') || errorsVisible('teamIds')) && departmentOrTeamMissing.value
        ? t('Team oder Abteilung erforderlich')
        : null,
);
const departmentIdsErrors = computed(() => serverFieldError('departmentIds') ?? departmentOrTeamError.value);
const teamIdsErrors = computed(() => serverFieldError('teamIds') ?? departmentOrTeamError.value);

function clearErrorsOnceValid() {
    if (isFormValid.value && (showValidationErrors.value || serverError.value)) {
        showValidationErrors.value = false;
        serverValidationErrors.value = null;
        serverError.value = null;
    }
}

watch(
    [title, teaser, text, clearing, selectedGroupIds, publishImmediately, publishDate, selectedDepartmentIds, selectedTeamIds],
    clearErrorsOnceValid,
);

function onSubmit() {
    if (isFormValid.value) {
        showValidationErrors.value = false;
        showSaveConfirm.value = true;
    } else {
        showValidationErrors.value = true;
    }
}

async function save() {
    showSaveConfirm.value = false;
    isSaving.value = true;
    serverError.value = null;
    serverValidationErrors.value = null;

    const payload = {
        title: title.value,
        teaser: teaser.value,
        text: text.value,
        clearing: clearing.value,
        groupIds: selectedGroupIds.value,
        departmentIds: selectedDepartmentIds.value,
        teamIds: selectedTeamIds.value,
        // Angular schließt das disabled publishDate-Control aus form.value aus
        // (sofort veröffentlichen oder bereits veröffentlicht).
        ...(publishDateDisabled.value ? {} : { publishDate: publishDate.value }),
        notify: notify.value,
    };

    try {
        let savedNews: NewsDetail;
        if (isEdit.value) {
            savedNews = (
                await apiFetch<{ data: NewsDetail }>(`/news/${newsId.value}`, {
                    method: 'PUT',
                    body: JSON.stringify(payload),
                })
            ).data;
        } else {
            savedNews = (
                await apiFetch<{ data: NewsDetail }>('/news', {
                    method: 'POST',
                    body: JSON.stringify(payload),
                })
            ).data;
        }

        if (newImageBlob.value) {
            await apiFetch(`/news/${savedNews.id}/image`, {
                method: 'PUT',
                body: newImageBlob.value,
                headers: { 'Content-Type': newImageBlob.value.type },
            }).catch(() => {
                serverError.value = 'Das Bild konnte leider nicht gespeichert werden. Alle anderen Änderungen wurden gespeichert.';
            });
        } else if (pendingImageDeletion.value && isEdit.value) {
            await apiFetch(`/news/${savedNews.id}/image`, { method: 'DELETE' }).catch(() => {});
        }

        if (!serverError.value) {
            router.push(localePath(`news/${savedNews.id}`));
        }
    } catch (e) {
        const validation = await parseValidationErrors(e);
        if (validation) {
            serverValidationErrors.value = validation;
            showValidationErrors.value = true;
        } else {
            serverError.value = 'Es ist ein unerwarteter Fehler aufgetreten';
            showValidationErrors.value = false;
            serverValidationErrors.value = null;
        }
    } finally {
        isSaving.value = false;
    }
}

async function deleteNews() {
    showDeleteConfirm.value = false;
    isDeleting.value = true;
    serverError.value = null;
    serverValidationErrors.value = null;
    try {
        await apiFetch(`/news/${newsId.value}`, { method: 'DELETE' });
        router.push(localePath('news'));
    } catch (e) {
        const validation = await parseValidationErrors(e);
        if (validation) {
            serverValidationErrors.value = validation;
            showValidationErrors.value = true;
        } else {
            serverError.value = 'Es ist ein unerwarteter Fehler aufgetreten';
            showValidationErrors.value = false;
            serverValidationErrors.value = null;
        }
    } finally {
        isDeleting.value = false;
    }
}

onMounted(async () => {
    // Angular instantiates kgr-news-form — and with it the group/department fetch — behind
    // `*kgrIfAppType="'employee'"`, a static gate. Bailing on `notAllowed` instead would also test
    // `canCreateNews`, which is still false at mount time because the user arrives with the async
    // initApp(): the early return then fired, no options were ever fetched, and the group/department
    // fields stayed empty for the rest of the session even though the form itself rendered.
    if (!isEdit.value && !isEmployeeApp) {
        isLoading.value = false;
        return;
    }
    try {
        // Angular holds `groups$` and `departments$`/`teams$` as streams that only the async pipe
        // subscribes — so each list is fetched exactly when its field renders, and never otherwise.
        // Loading both eagerly here made the club apps call `GET /groups`, which answers 404 for
        // them (no kindergarten groups). Both fetches are therefore gated on the same condition as
        // their field: the group field on the institution type, the department/team fields on the
        // selected clearing (see the watch below).
        if (isKindergarten) {
            await loadGroups();
        }

        if (isEdit.value) {
            const newsResult = await apiFetch<{ data: NewsDetail }>(`/news/${newsId.value}`);
            existingNews.value = newsResult.data;
            title.value = getOriginal(newsResult.data.title);
            teaser.value = getOriginal(newsResult.data.teaser);
            text.value = getOriginal(newsResult.data.text);
            clearing.value = newsResult.data.clearing ?? 'internal';
            selectedGroupIds.value = (newsResult.data.groups ?? []).map((g) => g.id);
            selectedDepartmentIds.value = (newsResult.data.departments ?? []).map((d) => d.id);
            selectedTeamIds.value = (newsResult.data.teams ?? []).map((team) => team.id);
            publishImmediately.value = false;
            publishDate.value = newsResult.data.publishDate ?? '';
        }
    } finally {
        isLoading.value = false;
    }

    // Angular's controls turn `dirty` on user input only — the seeding above uses `patchValue`,
    // which leaves them pristine. Registering the watches after the seed reproduces that.
    watch(title, () => markInteracted('title'));
    // null → value is the preselection above (possibly after mount), not user input.
    watch(clearing, (_, previous) => {
        if (previous !== null) {
            markInteracted('clearing');
        }
    });
    // Only the date itself: toggling „Sofort veröffentlichen" merely enables Angular's control
    // (`enable()`), which leaves it pristine — the empty field must not turn red on the toggle.
    watch(publishDate, () => markInteracted('publishDate'));
    watch(selectedDepartmentIds, () => markInteracted('departmentIds'));
    watch(selectedTeamIds, () => markInteracted('teamIds'));
});
</script>

<template>
    <KgrPageTitle
        :title="isEdit ? $t('News bearbeiten') : $t('News erstellen')"
        :link="localePath('news')"
    />

    <!-- Angular news-/event-form.component.html: `<div [kgrLoading]="state() === 'processing'">`
         umschließt das ganze Formular — der Spinner legt sich über den Inhalt, statt ihn zu
         ersetzen. Kein `blocking`: das setzt Angular hier nicht. -->
    <KgrLoadingContainer
        :loading="isSaving || isDeleting"
        class="kgr-region kgr-block kgr-block--vertical"
    >
        <div
            v-if="showValidationErrors"
            class="kgr-alert kgr-alert--error kgr-row"
        >
            {{ $t('Bitte überprüfen Sie Ihre Angaben.') }}
        </div>
        <div
            v-if="serverError"
            class="kgr-alert kgr-alert--error kgr-row"
        >
            {{ $t(serverError) }}
        </div>

        <KgrLoadingContainer
            v-if="isLoading"
            :loading="true"
        />
        <div
            v-else-if="notAllowed"
            class="kgr-alert kgr-alert--error kgr-row"
        >
            {{ $t('Sie haben keine Berechtigung, diesen Beitrag zu bearbeiten.') }}
        </div>
        <form
            v-else
            novalidate
            @submit.prevent="onSubmit"
        >
            <div class="kgr-grid kgr-grid--full kgr-grid--large-1of2 kgr-grid--bigGutters kgr-row kgr-row--bigMargin">
                <div class="kgr-grid__cell">
                    <KgrInputGroup
                        :label="$t('Titel')"
                        :required="true"
                        :errors="titleErrors"
                        :constrained-width="false"
                        :row="false"
                    >
                        <input
                            v-model="title"
                            type="text"
                            class="kgr-input"
                            @blur="markInteracted('title')"
                        />
                    </KgrInputGroup>

                    <KgrInputGroup
                        :label="$t('Kurzbeschreibung')"
                        :maxlength="TEASER_MAX_LENGTH"
                        :model-value="teaser"
                        :errors="teaserErrors"
                        :constrained-width="false"
                        :row="false"
                    >
                        <textarea
                            v-model="teaser"
                            class="kgr-input teaser"
                        ></textarea>
                    </KgrInputGroup>

                    <KgrInputGroup
                        tag="div"
                        :label="$t('Text')"
                        :errors="textErrors"
                        :constrained-width="false"
                        :row="false"
                    >
                        <KgrWysiwyg
                            v-model="text"
                            :height="400"
                        />
                    </KgrInputGroup>
                </div>

                <div class="kgr-grid__cell">
                    <KgrInputGroup
                        tag="div"
                        :label="$t('Bild')"
                        :errors="serverFieldError('image')"
                        :constrained-width="false"
                        :row="false"
                    >
                        <KgrEditableImage
                            :image-url="previewImageUrl"
                            :aspect-ratio="1.48511904762"
                            style="width: 190px"
                            @select="onImageSelect"
                            @request-delete="showImageDeleteConfirm = true"
                        />
                    </KgrInputGroup>

                    <KgrInputGroup
                        v-if="isKindergarten"
                        tag="div"
                        :label="$t('Gruppen zuweisen')"
                        :errors="serverFieldError('groupIds')"
                        :constrained-width="false"
                        :row="false"
                    >
                        <KgrSelectControlWithPills
                            v-model="selectedGroupIds"
                            :options="editableGroups"
                            value-key="id"
                            label-key="name"
                            :empty-label="$t('Alle Gruppen')"
                        />
                    </KgrInputGroup>

                    <KgrInputGroup
                        :label="$t('Freigabe')"
                        :required="true"
                        :errors="clearingErrors"
                        :constrained-width="false"
                        :row="false"
                    >
                        <KgrClearingSelect
                            v-model="clearing"
                            :allowed-to-manage="userStore.currentUser?.newsClearingsAllowedToManage"
                        />
                    </KgrInputGroup>

                    <template v-if="showDepartmentAndTeamFields">
                        <KgrInputGroup
                            v-if="editableDepartments.length > 0"
                            tag="div"
                            :label="$t('Freigabe für Mitglieder dieser Abteilungen')"
                            :errors="departmentIdsErrors"
                            :constrained-width="false"
                            :row="false"
                        >
                            <KgrSelectControlWithPills
                                v-model="selectedDepartmentIds"
                                :options="editableDepartments"
                                value-key="id"
                                label-key="hierarchyName"
                                sort-by-key="hierarchyName"
                                :empty-label="$t('Abteilung wählen')"
                                :searchable="true"
                            />
                        </KgrInputGroup>
                        <KgrInputGroup
                            v-if="editableTeams.length > 0"
                            tag="div"
                            :label="$t('Freigabe für Mitglieder dieser Teams')"
                            :errors="teamIdsErrors"
                            :constrained-width="false"
                            :row="false"
                        >
                            <KgrSelectControlWithPills
                                v-model="selectedTeamIds"
                                :options="editableTeams"
                                value-key="id"
                                label-key="hierarchyName"
                                sort-by-key="hierarchyName"
                                :empty-label="$t('Team wählen')"
                                :searchable="true"
                            />
                        </KgrInputGroup>
                    </template>

                    <KgrInputGroup
                        tag="div"
                        :label="$t('Publizierungsdatum')"
                        :required="publishDateVisible"
                        :errors="publishDateErrors"
                        :constrained-width="false"
                        :row="false"
                    >
                        <label
                            v-if="!existingNews?.isPublished"
                            class="kgr-inputGroup kgr-postfix kgr-postfix--gutters kgr-postfix--center kgr-row kgr-row--extraSmallMargin"
                        >
                            <div class="kgr-postfix__fixed">
                                <SphSwitch v-model="publishImmediately" />
                            </div>
                            <div class="kgr-postfix__fluid">{{ $t('Sofort veröffentlichen') }}</div>
                        </label>
                        <div
                            class="kgr-inputGroup kgr-hidden kgr-hidden--fadeInOnUnhidden"
                            :class="{ 'is-unhidden': publishDateVisible }"
                        >
                            <DatePicker
                                v-model="publishDate"
                                :disabled="existingNews?.isPublished"
                                model-type="iso-atom"
                                time-input
                            />
                        </div>
                    </KgrInputGroup>
                </div>
            </div>

            <div class="kgr-inputGroup kgr-postfix kgr-postfix--center kgr-postfix--gutters">
                <div class="kgr-postfix__fixed">
                    <button
                        type="button"
                        class="kgr-button kgr-button--outline"
                        @click="router.push(localePath('news'))"
                    >
                        {{ $t('Abbrechen') }}
                    </button>
                </div>
                <div
                    v-if="isEdit"
                    class="kgr-postfix__fixed"
                >
                    <button
                        type="button"
                        class="kgr-button kgr-button--danger kgr-button--outline"
                        @click="showDeleteConfirm = true"
                    >
                        {{ $t('Löschen') }}
                    </button>
                </div>
                <div class="kgr-postfix__fluid"></div>
                <div class="kgr-postfix__fixed">
                    <button
                        type="submit"
                        class="kgr-button kgr-button--accent"
                    >
                        {{ $t('Speichern') }}
                    </button>
                </div>
            </div>
        </form>
    </KgrLoadingContainer>

    <!-- Image cropper modal -->
    <KgrImageCropper
        v-if="showCropper && cropperSrc"
        :src="cropperSrc"
        :aspect-ratio="1.48511904762"
        :max-width="998"
        @confirm="onCropperConfirm"
        @cancel="onCropperCancel"
    >
        <template #header>
            <h3 class="kgr-headline kgr-headline--h3">
                {{ $t('Bild bearbeiten') }}
            </h3>
        </template>
    </KgrImageCropper>

    <!-- Image delete confirmation (Angular: kgr-editable-image's own modal, with the same preview) -->
    <KgrConfirmModal
        :show="showImageDeleteConfirm"
        :headline="$t('Bild löschen?')"
        :confirm-button-label="$t('Löschen')"
        confirm-button-css-class="kgr-button--danger"
        @cancel="showImageDeleteConfirm = false"
        @confirm="removeImage"
    >
        <KgrAuthImage
            v-if="previewImageUrl"
            :src="previewImageUrl"
            alt=""
            class="kgr-image kgr-image--rounded"
        />
    </KgrConfirmModal>

    <!-- Save confirmation modal -->
    <KgrConfirmModal
        :show="showSaveConfirm"
        :headline="$t('Änderungen speichern?')"
        @cancel="showSaveConfirm = false"
        @confirm="save"
    >
        <KgrInputGroup
            tag="div"
            :label="$t('Benachrichtigungen versenden')"
            :errors="serverFieldError('notify')"
            :constrained-width="false"
            :row="false"
        >
            <label class="kgr-inputGroup kgr-postfix kgr-postfix--center kgr-postfix--gutters">
                <SphSwitch
                    v-model="notify"
                    class="kgr-postfix__fixed"
                />
                <span class="kgr-postfix__fluid">
                    <template v-if="!publishDate">
                        {{
                            $t(
                                'Alle Personen, für die dieser Eintrag freigegeben ist, jetzt automatisch über die Erstellung / Änderung benachrichtigen',
                            )
                        }}
                    </template>
                    <template v-else>
                        {{
                            $t(
                                'Alle Personen, für die dieser Eintrag freigegeben ist, zum Zeitpunkt der Veröffentlichung automatisch über die Erstellung / Änderung benachrichtigen',
                            )
                        }}
                    </template>
                </span>
            </label>
        </KgrInputGroup>
    </KgrConfirmModal>

    <!-- Delete confirmation modal -->
    <KgrConfirmModal
        :show="showDeleteConfirm"
        :headline="$t('Beitrag löschen?')"
        :confirm-button-label="$t('Löschen')"
        confirm-button-css-class="kgr-button--danger"
        @cancel="showDeleteConfirm = false"
        @confirm="deleteNews"
    >
        <i18n-t
            keypath="Sind Sie sicher, dass Sie den News-Beitrag &quot;{title}&quot; löschen möchten?"
            tag="p"
        >
            <template #title>
                <strong class="kgr-text kgr-text--bold">{{ title }}</strong>
            </template>
        </i18n-t>
    </KgrConfirmModal>
</template>

<style scoped lang="scss">
.editableImage {
    position: relative;
    display: inline-block;
    width: 190px;
}

.editButton {
    position: absolute;
    top: 5px;
    right: 5px;
    z-index: 1;

    &.is-flyoutOpen :deep(.kgr-profileImage__editButtonFlyout) {
        transform: scale(1);
        pointer-events: all;
    }
}

.placeholder {
    padding: 60px;
    border: 1px solid var(--kgr-color-neutral);
    background-color: var(--kgr-color-neutral-lighter);
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.multiselectWithPills__selectedValuesWrapper {
    margin-top: var(--kgr-default-margin-tiny);
    text-align: left;
}

.teaser {
    min-height: 4.2em;
}
</style>
